import React, { useState, useEffect } from 'react';
import { Head, usePage } from '@inertiajs/react';
import { PageProps } from '@/types';
import { Workflow } from '@/types/workflow';
import WorkflowPageLayout from './WorkflowPageLayout';
import WorkflowTable from './Components/WorkflowTable';
import PrimaryButton from '@/Components/PrimaryButton';
import Spinner from '@/Components/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faProjectDiagram, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { getWorkflows, updateWorkflow, deleteWorkflow } from '@/services/workflowService';
import WorkflowFormModal from '@/Pages/Workflow/Components/WorkflowFormModal';
import { useConfirm } from '@/Components/ConfirmDialog';
import toast from 'react-hot-toast';
import Popover from '@/Components/Popover';

function EmptyState({isPaidUser, onCreate}: { isPaidUser: boolean; onCreate: () => void }) {
    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="text-center py-16">
                <FontAwesomeIcon icon={faProjectDiagram} className="text-gray-300 dark:text-gray-600 text-6xl mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No workflows yet</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    Create your first workflow to automatically translate and email your subtitles after extraction.
                </p>
                <Popover
                    disabled={isPaidUser}
                    trigger="hover"
                    placement="top"
                    content={
                        <div className="px-3 py-2 text-sm text-gray-500">
                            <p>Workflows are not available in the free trial.</p>
                        </div>
                    }
                >
                    <span>
                        <PrimaryButton
                            onClick={onCreate}
                            disabled={!isPaidUser}
                            className={`${!isPaidUser ? 'cursor-not-allowed' : ''}`}
                        >
                        <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                        <span>Create Your First Workflow</span>
                    </PrimaryButton>
                    </span>
                </Popover>
            </div>
        </div>
    );
}

export default function WorkflowsIndex() {
    const { auth } = usePage<PageProps>().props;
    const isPaidUser = !auth.user.is_trial;

    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [loading, setLoading] = useState(true);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingWorkflow, setEditingWorkflow] = useState<Workflow | undefined>(undefined);
    const confirm = useConfirm();

    useEffect(() => {
        loadWorkflows().catch(err => toast.error(err.message || 'Failed to load workflows'));
    }, []);

    useEffect(() => {
        if(!showFormModal && editingWorkflow) {
            setTimeout(() => setEditingWorkflow(undefined), 300); // Clear editing state after modal closes
        }
    }, [showFormModal, editingWorkflow]);

    const loadWorkflows = async () => {
        try {
            setLoading(true);
            const data = await getWorkflows();
            setWorkflows(data || []);
        } catch (error: any) {
            toast.error(error.message || 'Failed to load workflows');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateWorkflow = (workflow: Workflow) => {
        setWorkflows(prev => [workflow, ...prev]);
        setShowFormModal(false);
        toast.success('Workflow created successfully');
    };

    const handleEditWorkflow = (workflow: Workflow) => {
        setEditingWorkflow(workflow);
        setShowFormModal(true);
    };

    const handleUpdateWorkflow = async (updatedWorkflow: Workflow) => {
        try {
            const result = await updateWorkflow(updatedWorkflow.id, updatedWorkflow);
            setWorkflows(prev => {
                const isDefault = result.isDefault;
                return prev.map(w => {
                    return w.id === result.id ? result : isDefault ? { ...w, isDefault: false } : w;
                });
            });
            setShowFormModal(false);
            toast.success('Workflow updated successfully');
        } catch (error: any) {
            toast.error(error.message || 'Failed to update workflow');
        }
    };

    const handleDeleteWorkflow = async (workflow: Workflow) => {
        const confirmed = await confirm({
            title: 'Delete Workflow',
            message: `Are you sure you want to delete this workflow? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel'
        });

        if (!confirmed) return;

        try {
            await deleteWorkflow(workflow.id);
            setWorkflows(prev => prev.filter(w => w.id !== workflow.id));
            toast.success('Workflow deleted successfully');
        } catch (error: any) {
            toast.error(error.message || 'Failed to delete workflow');
        }
    };

    return (
        <>
            <Head title="Workflows">
                <meta name="description" content="Manage your automated workflow processes."/>
            </Head>
            <WorkflowPageLayout>
                <div className="p-6">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6 mt-5">
                        <div className="">
                            <h2 className="text-2xl  text-gray-900 dark:text-gray-100">My Workflows</h2>
                            <p className="text-gray-500 dark:text-gray-400 mt-1 hidden md:block">
                                Automate your subtitle processing with custom workflows.
                            </p>
                        </div>
                        {workflows.length > 0 && (
                            <Popover
                                disabled={isPaidUser}
                                trigger="hover"
                                placement="left"
                                content={
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                        <p>Workflows are not available in the free trial.</p>
                                    </div>
                                }
                            >
                                <PrimaryButton
                                    className="me-2"
                                    onClick={() => setShowFormModal(true)}
                                    disabled={!isPaidUser}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                                    Create <span className="hidden md:inline"> Workflow</span>
                                </PrimaryButton>
                            </Popover>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center h-32">
                            <Spinner className="w-4 h-4" />
                        </div>
                    ) : (workflows.length === 0 ? (
                           <EmptyState isPaidUser={isPaidUser} onCreate={() => setShowFormModal(true)} />
                         ) : (
                                <WorkflowTable
                                    workflows={workflows}
                                    onEdit={handleEditWorkflow}
                                    onDelete={handleDeleteWorkflow}
                                />
                            )
                    )}


                </div>

                <WorkflowFormModal
                    show={showFormModal}
                    onClose={() => {
                        setShowFormModal(false);
                    }}
                    onSave={editingWorkflow ? handleUpdateWorkflow : handleCreateWorkflow}
                    workflow={editingWorkflow}
                    showDescription={!editingWorkflow}
                    isPaidUser={isPaidUser}
                />
            </WorkflowPageLayout>
        </>
    );
}
